import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import type { Supplier, SupplierCreateInput, SupplierUpdateInput } from './types';

/**
 * Supplier (source company) Master API.
 *
 * Backed by `/api/companies` with source-supplier role and supplier party
 * filters. Mirrors the warehouses CRUD shape. The list endpoint accepts
 * `active_only=true` so the inbound dialog only shows usable rows.
 */

export interface SupplierListMeta {
  total: number;
  page: number;
  per_page: number;
}

export interface SupplierListResult {
  rows: Supplier[];
  meta: SupplierListMeta;
}

export interface SupplierListParams {
  active_only?: boolean;
  page?: number;
  per_page?: number;
}

export const listSuppliers = async (
  params: SupplierListParams = {},
): Promise<SupplierListResult> => {
  const res = await apiClient.get<ApiSuccess<Supplier[], SupplierListMeta>>('/companies', {
    params: { role: 'source_supplier', party_type: 'supplier', ...params },
  });
  const rows = res.data.data ?? [];
  return {
    rows,
    meta: res.data.meta ?? {
      total: rows.length,
      page: params.page ?? 1,
      per_page: params.per_page ?? rows.length,
    },
  };
};

export const createSupplier = async (body: SupplierCreateInput): Promise<Supplier> => {
  const res = await apiClient.post<ApiSuccess<Supplier>>('/companies', {
    ...body,
    company_role: 'source_supplier',
    party_type: 'supplier',
  });
  return res.data.data;
};

export const updateSupplier = async (id: string, body: SupplierUpdateInput): Promise<Supplier> => {
  const res = await apiClient.put<ApiSuccess<Supplier>>(`/companies/${id}`, body);
  return res.data.data;
};

export const deleteSupplier = async (id: string): Promise<void> => {
  await apiClient.delete(`/companies/${id}`);
};

/** Permanent removal. Only succeeds for a deactivated, unreferenced company. */
export const purgeSupplier = async (id: string): Promise<void> => {
  await apiClient.delete(`/companies/${id}/purge`);
};

export const activateSupplier = async (id: string): Promise<Supplier> => {
  const res = await apiClient.put<ApiSuccess<Supplier>>(`/companies/${id}`, { active_flag: true });
  return res.data.data;
};
